import { useEffect, useState, memo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import useApi from 'hooks/useApi';
import { useField } from 'formik';

interface Role {
  uid: string;
  roleName: string;
}

interface UserRoleProps {
  name: string;
  clientUid?: string; // optional client UID to scope roles like EscalationSettings
}

const UserRole = ({ name, clientUid }: UserRoleProps) => {
  const [rolesList, setRolesList] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { getResourceTypes } = useApi();

  const [field, , helpers] = useField<string[]>({ name });
  const { value = [] } = field;
  const { setValue } = helpers;

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        if (clientUid && getResourceTypes) {
          // Match EscalationSettings: use getResourceTypes(clientUid) and map name -> roleName
          const res = await getResourceTypes(clientUid, 'user');
          const roleData = res?.data;
          if (Array.isArray(roleData)) {
            const roleList = roleData.map((role: any) => ({
              uid: role.uid,
              roleName: role.name,
            }));
            setRolesList(roleList);
          } else {
            setRolesList([]);
          }
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        setRolesList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [clientUid, getResourceTypes]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    // Only filter out completely invalid values, keep existing valid ones
    const processedValue = typeof value === 'string' ? value.split(',') : value;
    setValue(processedValue);
  };

  return (
    <Box my={1}>
      <Typography variant="h6" gutterBottom>
        Select Roles For this Activity
      </Typography>
      <FormControl fullWidth size="medium">
        <Select
          multiple
          value={value}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <em>Select roles</em>;
            }
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected
                  .filter((uid) => uid && uid !== 'undefined' && uid.toString().trim() !== '') // Filter out invalid UIDs
                  .map((uid, index) => {
                    const role = rolesList.find((r) => r.uid === uid);
                    const chipKey = uid || `fallback-${index}`; // Ensure unique key
                    const chipLabel = role?.roleName || `Unknown Role (${uid})`; // Better fallback
                    return (
                      <Chip key={`role-chip-${chipKey}`} label={chipLabel} size="small" />
                    );
                  })}
              </Box>
            );
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 48 * 4.5 + 8,
                width: 250,
              },
            },
          }}
        >
          {loading ? (
            <MenuItem disabled>Loading roles...</MenuItem>
          ) : rolesList.length === 0 ? (
            <MenuItem disabled>No roles found</MenuItem>
          ) : (
            rolesList.map((role) => (
              <MenuItem key={role.uid} value={role.uid}>
                {role.roleName}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default memo(UserRole);
